import {
  FormChatAssistant,
  FormChatChoicePicker,
  FormChatNumberPicker,
  type ActivityChatSummary,
  type BankAccountChatSummary,
  type FormChatApiError,
  type FinancialChatSummary,
  type FormChatAssistantControl,
  type FormChatFieldInput,
  type FormChatMessage,
  type FormChatSelectionPayload,
  type FormChatSendOptions,
  type FormChatStep,
  type InventoryChatSummary,
  type MonthlyFeeChatSummary,
  type RitualBathChatSummary,
  type SpiritualChatSummary,
} from "gsintegrations";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { trackSpiritualProfileEvent } from "../analytics/spiritualProfile";
import { trackActivitiesEvent } from "../analytics/activities";
import { getActivityRoutes } from "../utils/activityTypeRoutes";
import { trackEventsEvent } from "../analytics/events";
import { trackFinancialEvent } from "../analytics/financial";
import { trackRitualBathsEvent } from "../analytics/ritualBaths";
import {
  trackInventoryError,
  trackInventoryEvent,
} from "../analytics/inventory";
import { useAuth } from "../auth/useAuth";
import { resolveInventoryChatCategories } from "../utils/inventoryChatCategories";
import { useActivityFormChatHelpers } from "../hooks/useActivityFormChatHelpers";
import { useEventFormChatHelpers } from "../hooks/useEventFormChatHelpers";
import { useFinancialFormChatHelpers } from "../hooks/useFinancialFormChatHelpers";
import { useMonthlyFeeFormChatHelpers } from "../hooks/useMonthlyFeeFormChatHelpers";
import { useBankAccountFormChatHelpers } from "../hooks/useBankAccountFormChatHelpers";
import { useShouldUseBankAccountChat } from "../hooks/useShouldUseBankAccountChat";
import { useRitualBathFormChatHelpers } from "../hooks/useRitualBathFormChatHelpers";
import { useShouldUseRitualBathChat } from "../hooks/useShouldUseRitualBathChat";
import { useSpiritualFormChatHelpers } from "../hooks/useSpiritualFormChatHelpers";
import { useCachedMedium } from "../hooks/useCachedMedium";
import { useCachedTerreiro } from "../hooks/useCachedTerreiro";
import { useFeatureLimit } from "../hooks/useFeatureLimit";
import { useFormChatMutation } from "../hooks/useFormChatMutation";
import { useFormChatSubmitActivity } from "../hooks/useFormChatSubmitActivity";
import { useFormChatSubmitInventory } from "../hooks/useFormChatSubmitInventory";
import { useHasPermission } from "../hooks/useHasPermission";
import { useProductAnalyticsBase } from "../hooks/useProductAnalyticsContext";
import { useProfileQuery } from "../hooks/useProfileQuery";
import { useInventoryListQuery } from "../hooks/useTerreiroInventoryListQuery";
import type {
  FormChatMessageRequest,
  FormChatMessageResponse,
  FormChatState,
} from "../repositories/formChat.repository";
import { formChatRepository } from "../repositories/formChat.repository";
import { extractAxiosApiError } from "../utils/extractAxiosApiError";
import { resolveDisplayedQuickReplies } from "../utils/formChatQuickReplies";
import { resolveRitualBathStep } from "../utils/ritualBathFormChatStep";
import { CHECKIN_WINDOW_EDIT_FIELDS } from "../utils/formChatEditPreview";
import {
  isListSelectField,
  resolveEditFieldInput,
  resolveFieldInput,
  resolveFieldOptions,
  resolveSelectedOption,
} from "../utils/formChatFieldOptions";
import { notifyLimitReached } from "../utils/limitToasts";
import { readReturnPath } from "../utils/navigationOrigin";
import { buildPathWithStatusTabSearch } from "../utils/statusTabNavigation";
import { isInventoryTabRoot } from "../pages/inventoryChrome";
import { registerFormChatAssistantOpen } from "./formChatAssistantBridge";
import {
  getActivityEditTargetId,
  getActivityModelTargetId,
  getBankAccountEditTargetId,
  getEventModelTargetId,
  getFinancialEditTarget,
  shouldLeaveFinancialEditOnCancel,
  getInventoryEditTargetId,
  getMonthlyFeeEditTargetId,
  getRitualBathEditTargetId,
  isActivityFormChatEnabled,
  isBankAccountFormChatEnabled,
  isEventFormChatEnabled,
  isFinancialFormChatEnabled,
  isFormChatCreateRoute,
  isInventoryFormChatEnabled,
  isRitualBathFormChatEnabled,
  isSpiritualFormChatEnabled,
  resolveFormChatContext,
  type FormChatContextConfig,
} from "./formChatContexts";

const HIDDEN_PREFIXES = [
  "/signin",
  "/signup",
  "/forgot-password",
  "/reset-password",
  "/onboarding",
];

// Quick replies the backend surfaces at the review step. They are handled via
// the summary card buttons instead, so we hide them from the chip row to avoid
// sending them back as plain-text messages (which the backend cannot act on).
// Fields the user can edit from the review card. Keys must match the backend
// InventoryChatState so `edit_field` targets the right field.
const EDITABLE_FIELDS: Array<{ key: string; label: string }> = [
  { key: "name", label: "Nome" },
  { key: "category", label: "Categoria" },
  { key: "unit", label: "Unidade" },
  { key: "quantity", label: "Quantidade" },
  { key: "minimumQuantity", label: "Quantidade mínima" },
  { key: "estimatedUnitCost", label: "Preço unitário" },
  { key: "location", label: "Localização" },
  { key: "image", label: "Foto" },
  { key: "description", label: "Descrição" },
];

type EditMode = "none" | "select-field" | "await-value";

const createMessage = (
  role: "assistant" | "user" | "system",
  content: string,
): FormChatMessage => ({
  id: `${role}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  role,
  content,
  createdAt: new Date().toISOString(),
});

const buildInitialMessages = (
  context: FormChatContextConfig | null,
): FormChatMessage[] =>
  context ? [createMessage("assistant", context.initialMessage)] : [];

const appEnv = import.meta.env ?? {};

const isFormChatHiddenRoute = (pathname: string) =>
  HIDDEN_PREFIXES.some((prefix) => pathname.startsWith(prefix));

const isFormChatFeatureEnabled = (
  activeContext: FormChatContextConfig | null,
) => {
  if (!activeContext) return false;
  if (activeContext.entityType === "event") {
    return isEventFormChatEnabled();
  }
  if (activeContext.entityType === "activity") {
    return isActivityFormChatEnabled();
  }
  if (activeContext.entityType === "spiritual_profile") {
    return isSpiritualFormChatEnabled();
  }
  if (
    activeContext.entityType === "income" ||
    activeContext.entityType === "outcome" ||
    activeContext.entityType === "monthly_fee"
  ) {
    return isFinancialFormChatEnabled();
  }
  if (activeContext.entityType === "bank_account") {
    return isBankAccountFormChatEnabled();
  }
  if (activeContext.entityType === "ritual_bath") {
    return isRitualBathFormChatEnabled();
  }
  return isInventoryFormChatEnabled();
};

const FormChatAssistantProvider = () => {
  const location = useLocation();
  const auth = useAuth();

  if (auth.isLoading || !auth.isAuthenticated) {
    return null;
  }

  if (isFormChatHiddenRoute(location.pathname)) {
    return null;
  }

  const activeContext = resolveFormChatContext(
    location.pathname,
    location.search,
  );
  if (!activeContext || !isFormChatFeatureEnabled(activeContext)) {
    return null;
  }

  return <FormChatAssistantProviderActive activeContext={activeContext} />;
};

interface FormChatAssistantProviderActiveProps {
  activeContext: FormChatContextConfig;
}

const FormChatAssistantProviderActive = ({
  activeContext,
}: FormChatAssistantProviderActiveProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const productBase = useProductAnalyticsBase();
  const { id: terreiroId } = useCachedTerreiro() ?? {};
  const { id: mediumId } = useCachedMedium() ?? {};
  const isCreateRoute = isFormChatCreateRoute(location.pathname);
  const { data: profile } = useProfileQuery();
  const userAvatarUrl = profile?.profile?.avatarUrl;

  const isActivityContext = activeContext.entityType === "activity";
  const isEventContext = activeContext.entityType === "event";
  const isSpiritualContext = activeContext.entityType === "spiritual_profile";
  const isFinancialContext =
    activeContext.entityType === "income" ||
    activeContext.entityType === "outcome";
  const isMonthlyFeeContext = activeContext.entityType === "monthly_fee";
  const isBankAccountContext = activeContext.entityType === "bank_account";
  const isRitualBathContext = activeContext.entityType === "ritual_bath";
  const financialFlow =
    activeContext.entityType === "outcome" ? "outcome" : "income";
  const financialAnalyticsType = isMonthlyFeeContext
    ? "monthly_fee"
    : isBankAccountContext
      ? "bank_account"
      : financialFlow;
  const isPersonalProfile = !terreiroId && Boolean(mediumId);
  const activityEditTargetId =
    activeContext.key === "activity_edit"
      ? getActivityEditTargetId(location.pathname)
      : undefined;
  const activityModelTargetId =
    activeContext.key === "activity_model"
      ? getActivityModelTargetId(location.pathname)
      : undefined;
  const eventModelTargetId =
    activeContext.key === "event_model"
      ? getEventModelTargetId(location.pathname)
      : undefined;
  const modelTargetId = activityModelTargetId ?? eventModelTargetId;
  const inventoryEditTargetId =
    activeContext.key === "inventory_edit"
      ? getInventoryEditTargetId(location.pathname)
      : undefined;
  const financialEditTargetId =
    activeContext.key === "income_edit" || activeContext.key === "outcome_edit"
      ? getFinancialEditTarget(location.pathname)?.id
      : undefined;
  const monthlyFeeEditTargetId =
    activeContext.key === "monthly_fee_edit"
      ? getMonthlyFeeEditTargetId(location.pathname)
      : undefined;
  const bankAccountEditTargetId =
    activeContext.key === "bank_account_edit"
      ? getBankAccountEditTargetId(location.pathname)
      : undefined;
  const ritualBathEditTargetId =
    activeContext.key === "ritual_bath_edit"
      ? getRitualBathEditTargetId(location.pathname)
      : undefined;
  const editTargetId =
    activityEditTargetId ??
    inventoryEditTargetId ??
    financialEditTargetId ??
    monthlyFeeEditTargetId ??
    bankAccountEditTargetId ??
    ritualBathEditTargetId;
  const isEditRoute = Boolean(editTargetId);
  const seedTargetId = editTargetId ?? modelTargetId;
  const isFullPageRoute =
    isCreateRoute || isEditRoute || Boolean(modelTargetId);
  const [flowOpened, setFlowOpened] = useState(false);
  const chatDataEnabled = isFullPageRoute || flowOpened;
  const isInventoryContext = activeContext.entityType === "inventory_item";

  const inventoryPermission = useHasPermission(
    terreiroId,
    "inventory.manage_items",
  );
  const activityPermission = useHasPermission(
    terreiroId,
    activeContext.requiredPermission,
    mediumId,
  );
  const activityOwnPermission = useHasPermission(
    undefined,
    activeContext.requiredPermissionOwn ?? "activity.create.own",
    mediumId,
  );
  const eventPermission = useHasPermission(terreiroId, "event.create");
  const spiritualPermission = useHasPermission(
    terreiroId,
    "spiritual_profile.manage_self",
    mediumId,
  );
  const hasContextPermission = isSpiritualContext
    ? spiritualPermission
    : isEventContext
      ? eventPermission
      : isActivityContext ||
          isFinancialContext ||
          isMonthlyFeeContext ||
          isBankAccountContext ||
          isRitualBathContext
        ? isPersonalProfile
          ? activityOwnPermission
          : activityPermission
        : inventoryPermission;
  const { mutateAsync: sendMessageMutation, isPending } = useFormChatMutation();
  const { submitInventoryByChat, updateInventoryByChat } =
    useFormChatSubmitInventory(inventoryEditTargetId);
  const { submitActivityByChat } = useFormChatSubmitActivity();
  const inventoryLimit = useFeatureLimit({
    feature: "maxInventoryItems",
    enabled: chatDataEnabled && isInventoryContext,
  });
  const activityLimit = useFeatureLimit({
    feature: "maxActivities",
    enabled: chatDataEnabled && isActivityContext,
  });
  const eventLimit = useFeatureLimit({
    feature: "maxEvents",
    enabled: chatDataEnabled && isEventContext,
  });
  const financialLimitFeature =
    financialFlow === "outcome"
      ? ("maxOutcomeTransactions" as const)
      : ("maxIncomingTransactions" as const);
  const financialLimit = useFeatureLimit({
    feature: financialLimitFeature,
    enabled: chatDataEnabled && isFinancialContext,
  });
  const canCreate = isEditRoute || isBankAccountContext || isRitualBathContext
    ? true
    : isSpiritualContext
      ? spiritualPermission
      : isFinancialContext
        ? financialLimit.canCreate
        : isEventContext
          ? eventLimit.canCreate
          : isActivityContext
            ? activityLimit.canCreate
            : inventoryLimit.canCreate;
  const isAtLimit =
    isSpiritualContext || isEditRoute || isBankAccountContext || isRitualBathContext
    ? false
    : isFinancialContext
      ? financialLimit.isAtLimit
      : isEventContext
        ? eventLimit.isAtLimit
        : isActivityContext
          ? activityLimit.isAtLimit
          : inventoryLimit.isAtLimit;
  const limitMessage = isFinancialContext
    ? financialLimit.message
    : isEventContext
      ? eventLimit.message
      : isActivityContext
        ? activityLimit.message
        : inventoryLimit.message;
  const limitFeature = isFinancialContext
    ? financialLimitFeature
    : isEventContext
      ? ("maxEvents" as const)
      : isActivityContext
        ? ("maxActivities" as const)
        : ("maxInventoryItems" as const);
  const { data: inventoryItems = [] } = useInventoryListQuery(
    terreiroId,
    mediumId,
    undefined,
    { enabled: chatDataEnabled && isInventoryContext },
  );

  const knownLocations = useMemo(() => {
    const locations = (inventoryItems as Array<{ location?: string }>)
      .map((item) => item.location?.trim())
      .filter((location): location is string => Boolean(location));
    return [...new Set(locations)];
  }, [inventoryItems]);

  const knownCategories = useMemo(
    () =>
      resolveInventoryChatCategories(
        inventoryItems as Array<{ category?: string | null }>,
      ),
    [inventoryItems],
  );

  const inventoryReferenceData = useMemo(
    () => ({
      categories: knownCategories.map((name) => ({ id: name, name })),
    }),
    [knownCategories],
  );

  const [sessionId, setSessionId] = useState<string | undefined>();
  const [messages, setMessages] = useState<FormChatMessage[]>(() =>
    buildInitialMessages(activeContext),
  );
  const [formState, setFormState] = useState<FormChatState>({});
  const [quickReplies, setQuickReplies] = useState<string[]>([]);
  const [summary, setSummary] = useState<
    InventoryChatSummary | ActivityChatSummary | undefined
  >();
  const [currentStep, setCurrentStep] = useState<FormChatStep>("basic-info");
  const [activeField, setActiveField] = useState<string | undefined>(
    activeContext.initialActiveField,
  );
  const [fieldInput, setFieldInput] = useState<
    FormChatFieldInput | undefined
  >();
  const [error, setError] = useState<FormChatApiError | null>(null);
  const [editMode, setEditMode] = useState<EditMode>("none");
  const [editingField, setEditingField] = useState<string | null>(null);
  const [descriptionAutoGenerated, setDescriptionAutoGenerated] = useState(false);
  const [dirtyFields, setDirtyFields] = useState<string[]>([]);
  const [inventoryImage, setInventoryImage] = useState<File | null>(null);
  const inventoryImagePreviewUrl = useMemo(
    () => (inventoryImage ? URL.createObjectURL(inventoryImage) : null),
    [inventoryImage],
  );
  useEffect(
    () => () => {
      if (inventoryImagePreviewUrl) URL.revokeObjectURL(inventoryImagePreviewUrl);
    },
    [inventoryImagePreviewUrl],
  );
  useEffect(() => {
    if (formState.image !== "attached") setInventoryImage(null);
  }, [formState.image]);

  // Reset the conversation whenever the page context changes so the assistant
  // always reflects the entity the current page is asking about.
  useEffect(() => {
    setFlowOpened(false);
  }, [activeContext.key]);

  useEffect(() => {
    if (isFullPageRoute) return;
    const onPointerDown = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const label = target.closest("button")?.getAttribute("aria-label");
      if (
        label === "Abrir assistente" ||
        label === "Adicionar" ||
        label === "Adicionar evento"
      ) {
        setFlowOpened(true);
      }
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [isFullPageRoute]);

  const baseContextKey = activeContext.activityType
    ? `${activeContext.key}:${activeContext.activityType}`
    : activeContext.key;
  const activeContextKey = seedTargetId
    ? `${baseContextKey}:${seedTargetId}`
    : baseContextKey;
  const previousContextKey = useRef<string | null>(activeContextKey);
  const assistantControlRef = useRef<FormChatAssistantControl | null>(null);
  useEffect(() => {
    if (previousContextKey.current === activeContextKey) return;
    previousContextKey.current = activeContextKey;
    setSessionId(undefined);
    setMessages(buildInitialMessages(activeContext));
    setFormState({});
    setQuickReplies([]);
    setSummary(undefined);
    setActiveField(activeContext.initialActiveField);
    setFieldInput(undefined);
    setCurrentStep("basic-info");
    setError(null);
    setEditMode("none");
    setEditingField(null);
    setDescriptionAutoGenerated(false);
    setDirtyFields([]);
  }, [activeContext, activeContextKey]);

  const isVoiceEnabled = appEnv.VITE_FORM_CHAT_VOICE_ENABLED !== "false";
  const shouldEnableVoice =
    isVoiceEnabled &&
    !isActivityContext &&
    !isEventContext &&
    !isSpiritualContext;
  const hasOwnerScope =
    isActivityContext ||
    isSpiritualContext ||
    isFinancialContext ||
    isBankAccountContext ||
    isRitualBathContext
      ? Boolean(terreiroId || mediumId)
      : Boolean(terreiroId);
  const bankAccountChat = useShouldUseBankAccountChat({
    accountId: bankAccountEditTargetId,
    enabled: isBankAccountContext,
  });
  const ritualBathChat = useShouldUseRitualBathChat({
    bathId: ritualBathEditTargetId,
    enabled: isRitualBathContext,
  });
  const canShowAssistant =
    hasOwnerScope &&
    Boolean(hasContextPermission) &&
    !(isFinancialContext && isAtLimit && !chatDataEnabled) &&
    !(isBankAccountContext && !bankAccountChat.shouldUseChat) &&
    !(isRitualBathContext && !ritualBathChat.shouldUseChat);

  const handleCloseCreateChat = useCallback(() => {
    if (isRitualBathContext) {
      navigate(
        ritualBathEditTargetId
          ? `/ritual-baths/${ritualBathEditTargetId}`
          : (readReturnPath(location.state) ?? "/ritual-baths"),
      );
      return;
    }
    if (isBankAccountContext) {
      navigate(readReturnPath(location.state) ?? "/financial");
      return;
    }
    if (isMonthlyFeeContext) {
      navigate(readReturnPath(location.state) ?? "/financial/monthly-fee");
      return;
    }
    if (isFinancialContext) {
      navigate(
        readReturnPath(location.state) ??
          (financialFlow === "outcome" ? "/financial/outcome" : "/financial/income"),
      );
      return;
    }
    if (inventoryEditTargetId) {
      navigate(`/inventory/${inventoryEditTargetId}`, { state: location.state });
      return;
    }
    if (activityEditTargetId) {
      navigate(
        buildPathWithStatusTabSearch(
          getActivityRoutes(activeContext.activityType).detail(
            activityEditTargetId,
          ),
          location.search,
          { rebalance: false },
        ),
        { state: location.state },
      );
      return;
    }
    if (activityModelTargetId) {
      navigate(
        buildPathWithStatusTabSearch(
          getActivityRoutes(activeContext.activityType).detail(
            activityModelTargetId,
          ),
          location.search,
          { rebalance: false },
        ),
        { state: location.state },
      );
      return;
    }
    if (eventModelTargetId) {
      navigate(
        buildPathWithStatusTabSearch("/events", location.search, {
          rebalance: false,
        }),
      );
      return;
    }
    if (!isCreateRoute) return;
    if (isEventContext) {
      navigate(buildPathWithStatusTabSearch("/events", location.search));
      return;
    }
    if (isActivityContext) {
      navigate(
        buildPathWithStatusTabSearch(
          getActivityRoutes(activeContext.activityType).list,
          location.search,
        ),
      );
      return;
    }
    if (isSpiritualContext) {
      navigate("/spiritual-profile");
      return;
    }
    navigate("/inventory/list");
  }, [
    activeContext.activityType,
    activityEditTargetId,
    activityModelTargetId,
    eventModelTargetId,
    financialFlow,
    inventoryEditTargetId,
    isActivityContext,
    isCreateRoute,
    isEventContext,
    isBankAccountContext,
    isFinancialContext,
    isMonthlyFeeContext,
    isRitualBathContext,
    isSpiritualContext,
    location.search,
    location.state,
    navigate,
    ritualBathEditTargetId,
  ]);

  const handleResponseUpdate = useCallback(
    (response: FormChatMessageResponse) => {
      setSessionId(response.sessionId);
      setFormState(response.formState || {});
      setQuickReplies(response.quickReplies || []);
      setSummary(response.summary);
      setActiveField(response.activeField);
      setFieldInput(response.fieldInput);
      const nextStep = isRitualBathContext
        ? resolveRitualBathStep(response.currentStep, response.activeField)
        : response.currentStep;
      setCurrentStep(nextStep);
      setDescriptionAutoGenerated(response.descriptionAutoGenerated ?? false);
      setDirtyFields(response.dirtyFields ?? []);
      setEditMode("none");
      setEditingField(null);
      setMessages((prev) => {
        const lastAssistant = [...prev]
          .reverse()
          .find((message) => message.role === "assistant");
        if (lastAssistant?.content === response.assistantMessage) {
          return prev;
        }
        const assistantContent =
          typeof response.assistantMessage === "string"
            ? response.assistantMessage
            : "Não foi possível processar a resposta do assistente.";
        return [...prev, createMessage("assistant", assistantContent)];
      });
      if (nextStep === "review") {
        if (isFinancialContext || isMonthlyFeeContext || isBankAccountContext) {
          trackFinancialEvent("financial_form_chat_completed", productBase, {
            transaction_type: financialAnalyticsType,
          });
        } else if (isEventContext) {
          trackEventsEvent("event_form_chat_completed", productBase);
        } else if (isActivityContext) {
          trackActivitiesEvent("activity_form_chat_completed", productBase);
        } else if (isSpiritualContext) {
          trackSpiritualProfileEvent("spiritual_profile_form_chat_completed", {
            terreiroId,
            mediumId,
            sourcePage: productBase.sourcePage,
          });
        } else if (isRitualBathContext) {
          trackRitualBathsEvent("ritual_bath_form_chat_completed", productBase, {
            source: "form_chat",
          });
        } else {
          trackInventoryEvent("form_chat_completed", productBase);
        }
      }
    },
    [
      financialAnalyticsType,
      isActivityContext,
      isEventContext,
      isFinancialContext,
      isBankAccountContext,
      isMonthlyFeeContext,
      isRitualBathContext,
      isSpiritualContext,
      mediumId,
      productBase,
      terreiroId,
    ],
  );

  const activityHelpers = useActivityFormChatHelpers({
    sessionId,
    messages,
    activeField,
    formState,
    summary: summary as ActivityChatSummary | undefined,
    descriptionAutoGenerated,
    onResponse: handleResponseUpdate,
    onUserMessage: (content) =>
      setMessages((prev) => [...prev, createMessage("user", content)]),
    onError: (nextError) => {
      setError(nextError);
      setMessages((prev) => [
        ...prev,
        createMessage("system", nextError.message),
      ]);
    },
    onReset: () => {
      setSessionId(undefined);
      setMessages(buildInitialMessages(activeContext));
      setFormState({});
      setQuickReplies([]);
      setSummary(undefined);
      setActiveField(activeContext.initialActiveField);
      setFieldInput(undefined);
      setCurrentStep("basic-info");
      setError(null);
      setEditMode("none");
      setEditingField(null);
      setDescriptionAutoGenerated(false);
    },
    onEdit: () => {
      if (currentStep !== "review") return;
      setEditMode("select-field");
      setEditingField(null);
    },
    onCloseAssistant: () => assistantControlRef.current?.close(),
    editTargetId: activityEditTargetId,
    modelTargetId: activityModelTargetId,
    activityType: activeContext.activityType,
    enabled: chatDataEnabled && isActivityContext,
  });

  const sendActivityRequestRef = useRef(activityHelpers.sendActivityRequest);
  sendActivityRequestRef.current = activityHelpers.sendActivityRequest;

  const eventHelpers = useEventFormChatHelpers({
    sessionId,
    activeField,
    formState,
    summary: summary as import("gsintegrations").EventChatSummary | undefined,
    descriptionAutoGenerated,
    onResponse: handleResponseUpdate,
    onUserMessage: (content) =>
      setMessages((prev) => [...prev, createMessage("user", content)]),
    onError: (nextError) => {
      setError(nextError);
      setMessages((prev) => [
        ...prev,
        createMessage("system", nextError.message),
      ]);
    },
    onReset: () => {
      setSessionId(undefined);
      setMessages(buildInitialMessages(activeContext));
      setFormState({});
      setQuickReplies([]);
      setSummary(undefined);
      setActiveField(activeContext.initialActiveField);
      setFieldInput(undefined);
      setCurrentStep("basic-info");
      setError(null);
      setEditMode("none");
      setEditingField(null);
      setDescriptionAutoGenerated(false);
    },
    onEdit: () => {
      if (currentStep !== "review") return;
      setEditMode("select-field");
      setEditingField(null);
    },
    onCloseAssistant: () => assistantControlRef.current?.close(),
    modelTargetId: eventModelTargetId,
    enabled: chatDataEnabled && isEventContext,
  });

  const sendEventRequestRef = useRef(eventHelpers.sendEventRequest);
  sendEventRequestRef.current = eventHelpers.sendEventRequest;
  const sendInventoryRequestRef = useRef<
    ((payload: { message: string; action: "resume" }) => Promise<
      FormChatMessageResponse | undefined
    >) | null
  >(null);
  const sendFinancialRequestRef = useRef<
    ((payload: { message: string; action: "resume" }) => Promise<
      FormChatMessageResponse | undefined
    >) | null
  >(null);
  const sendMonthlyFeeRequestRef = useRef<
    ((payload: { message: string; action: "resume" }) => Promise<
      FormChatMessageResponse | undefined
    >) | null
  >(null);
  const sendBankAccountRequestRef = useRef<
    ((payload: { message: string; action: "resume" }) => Promise<
      FormChatMessageResponse | undefined
    >) | null
  >(null);
  const sendRitualBathRequestRef = useRef<
    ((payload: { message: string; action: "resume" }) => Promise<
      FormChatMessageResponse | undefined
    >) | null
  >(null);
  const seededTargetRef = useRef<string | null>(null);
  useEffect(() => {
    if (!seedTargetId || !canShowAssistant) return;
    const seedKey = `${activeContext.key}:${seedTargetId}`;
    if (seededTargetRef.current === seedKey) return;
    // Deferred so a StrictMode remount cannot detach the mutation observer
    // mid-request, which leaves `isPending` stuck and the edit options disabled.
    const timer = window.setTimeout(() => {
      seededTargetRef.current = seedKey;
      const resume = { message: "", action: "resume" as const };
      const request = ritualBathEditTargetId
        ? (sendRitualBathRequestRef.current?.(resume) ??
          Promise.resolve(undefined))
        : bankAccountEditTargetId
        ? (sendBankAccountRequestRef.current?.(resume) ??
          Promise.resolve(undefined))
        : monthlyFeeEditTargetId
        ? (sendMonthlyFeeRequestRef.current?.(resume) ??
          Promise.resolve(undefined))
        : financialEditTargetId
        ? (sendFinancialRequestRef.current?.(resume) ??
          Promise.resolve(undefined))
        : inventoryEditTargetId
        ? (sendInventoryRequestRef.current?.(resume) ??
          Promise.resolve(undefined))
        : eventModelTargetId
          ? sendEventRequestRef.current(resume)
          : sendActivityRequestRef.current(resume);
      request
        .then((response) => {
          if (!editTargetId || response?.currentStep !== "review") return;
          setEditMode("select-field");
          setEditingField(null);
        })
        .catch(() => {
          seededTargetRef.current = null;
        });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [
    activeContext.key,
    bankAccountEditTargetId,
    canShowAssistant,
    editTargetId,
    eventModelTargetId,
    financialEditTargetId,
    inventoryEditTargetId,
    monthlyFeeEditTargetId,
    ritualBathEditTargetId,
    seedTargetId,
  ]);

  const spiritualHelpers = useSpiritualFormChatHelpers({
    sessionId,
    formState,
    summary: summary as SpiritualChatSummary | undefined,
    onResponse: handleResponseUpdate,
    onError: (nextError) => {
      setError(nextError);
      setMessages((prev) => [
        ...prev,
        createMessage("system", nextError.message),
      ]);
    },
    onReset: () => {
      setSessionId(undefined);
      setMessages(buildInitialMessages(activeContext));
      setFormState({});
      setQuickReplies([]);
      setSummary(undefined);
      setActiveField(activeContext.initialActiveField);
      setFieldInput(undefined);
      setCurrentStep("basic-info");
      setError(null);
      setEditMode("none");
      setEditingField(null);
      setDescriptionAutoGenerated(false);
    },
    onEdit: () => {
      if (currentStep !== "review") return;
      setEditMode("select-field");
      setEditingField(null);
    },
    onCloseAssistant: () => assistantControlRef.current?.close(),
  });

  const financialHelpers = useFinancialFormChatHelpers({
    flow: financialFlow,
    enabled: chatDataEnabled && isFinancialContext,
    editTargetId: financialEditTargetId,
    prefillInventoryItemId: (
      location.state as { inventoryItemId?: string } | null
    )?.inventoryItemId,
    confirmLabel: activeContext.confirmLabel,
    sessionId,
    formState,
    summary: isFinancialContext
      ? (summary as FinancialChatSummary | undefined)
      : undefined,
    onResponse: handleResponseUpdate,
    onError: (nextError) => {
      setError(nextError);
      setMessages((prev) => [
        ...prev,
        createMessage("system", nextError.message),
      ]);
    },
    onReset: () => {
      setSessionId(undefined);
      setMessages(buildInitialMessages(activeContext));
      setFormState({});
      setQuickReplies([]);
      setSummary(undefined);
      setActiveField(activeContext.initialActiveField);
      setFieldInput(undefined);
      setCurrentStep("basic-info");
      setError(null);
      setEditMode("none");
      setEditingField(null);
      setDescriptionAutoGenerated(false);
    },
    onEdit: () => {
      if (currentStep !== "review") return;
      setEditMode("select-field");
      setEditingField(null);
    },
  });
  sendFinancialRequestRef.current = financialHelpers.sendFinancialRequest;

  const monthlyFeeHelpers = useMonthlyFeeFormChatHelpers({
    enabled: isMonthlyFeeContext,
    monthlyFeeId: monthlyFeeEditTargetId,
    confirmLabel: activeContext.confirmLabel,
    sessionId,
    formState,
    summary: isMonthlyFeeContext
      ? (summary as MonthlyFeeChatSummary | undefined)
      : undefined,
    onResponse: handleResponseUpdate,
    onError: (nextError) => {
      setError(nextError);
      setMessages((prev) => [
        ...prev,
        createMessage("system", nextError.message),
      ]);
    },
    onReset: () => {
      setSessionId(undefined);
      setMessages(buildInitialMessages(activeContext));
      setFormState({});
      setQuickReplies([]);
      setSummary(undefined);
      setActiveField(activeContext.initialActiveField);
      setFieldInput(undefined);
      setCurrentStep("basic-info");
      setError(null);
      setEditMode("none");
      setEditingField(null);
      setDescriptionAutoGenerated(false);
    },
    onEdit: () => {
      if (currentStep !== "review") return;
      setEditMode("select-field");
      setEditingField(null);
    },
  });
  sendMonthlyFeeRequestRef.current = monthlyFeeHelpers.sendMonthlyFeeRequest;

  const bankAccountHelpers = useBankAccountFormChatHelpers({
    enabled: isBankAccountContext,
    editTargetId: bankAccountEditTargetId,
    confirmLabel: activeContext.confirmLabel,
    sessionId,
    formState,
    summary: isBankAccountContext
      ? (summary as BankAccountChatSummary | undefined)
      : undefined,
    onResponse: handleResponseUpdate,
    onError: (nextError) => {
      setError(nextError);
      setMessages((prev) => [
        ...prev,
        createMessage("system", nextError.message),
      ]);
    },
    onReset: () => {
      setSessionId(undefined);
      setMessages(buildInitialMessages(activeContext));
      setFormState({});
      setQuickReplies([]);
      setSummary(undefined);
      setActiveField(activeContext.initialActiveField);
      setFieldInput(undefined);
      setCurrentStep("basic-info");
      setError(null);
      setEditMode("none");
      setEditingField(null);
      setDescriptionAutoGenerated(false);
    },
    onEdit: () => {
      if (currentStep !== "review") return;
      setEditMode("select-field");
      setEditingField(null);
    },
  });
  sendBankAccountRequestRef.current = bankAccountHelpers.sendBankAccountRequest;
  const isBankAccountLimitBlocked =
    isBankAccountContext &&
    !bankAccountEditTargetId &&
    (bankAccountHelpers.isLimitLoading || !bankAccountHelpers.canCreate);

  const ritualBathHelpers = useRitualBathFormChatHelpers({
    enabled: isRitualBathContext,
    editTargetId: ritualBathEditTargetId,
    confirmLabel: activeContext.confirmLabel,
    sessionId,
    formState,
    dirtyFields,
    summary: isRitualBathContext
      ? (summary as RitualBathChatSummary | undefined)
      : undefined,
    onResponse: handleResponseUpdate,
    onError: (nextError) => {
      setError(nextError);
      setMessages((prev) => [
        ...prev,
        createMessage("system", nextError.message),
      ]);
    },
    onReset: () => {
      setSessionId(undefined);
      setMessages(buildInitialMessages(activeContext));
      setFormState({});
      setQuickReplies([]);
      setSummary(undefined);
      setActiveField(activeContext.initialActiveField);
      setFieldInput(undefined);
      setCurrentStep("basic-info");
      setError(null);
      setEditMode("none");
      setEditingField(null);
      setDescriptionAutoGenerated(false);
      setDirtyFields([]);
    },
    onEdit: () => {
      if (currentStep !== "review") return;
      setEditMode("select-field");
      setEditingField(null);
    },
  });
  sendRitualBathRequestRef.current = ritualBathHelpers.sendRitualBathRequest;
  const isRitualBathLimitBlocked =
    isRitualBathContext &&
    !ritualBathEditTargetId &&
    (ritualBathHelpers.isLimitLoading || !ritualBathHelpers.canCreate);

  const financialEditableFields = isFinancialContext
    ? financialHelpers.editableFields
    : isMonthlyFeeContext
      ? monthlyFeeHelpers.editableFields
      : isBankAccountContext
        ? bankAccountHelpers.editableFields
        : isRitualBathContext
          ? ritualBathHelpers.editableFields
          : null;

  const sendChatRequest = useCallback(
    async (
      payload: Omit<
        FormChatMessageRequest,
        | "entityType"
        | "knownLocations"
        | "knownCategories"
        | "referenceData"
        | "isPersonalProfile"
      >,
    ): Promise<FormChatMessageResponse | undefined> => {
      if (!activeContext) return undefined;
      if (isSpiritualContext) {
        if (!canCreate) return undefined;
      } else if (!canCreate || isAtLimit) {
        notifyLimitReached({
          feature: limitFeature,
          message: limitMessage,
        });
        return undefined;
      }

      if (isEventContext) {
        if (!terreiroId) return undefined;
        try {
          setError(null);
          return await eventHelpers.sendEventRequest(payload);
        } catch {
          return undefined;
        }
      }

      if (isActivityContext) {
        if (!terreiroId && !mediumId) return undefined;
        try {
          setError(null);
          return await activityHelpers.sendActivityRequest(payload);
        } catch {
          return undefined;
        }
      }

      if (isSpiritualContext) {
        if (!terreiroId && !mediumId) return undefined;
        try {
          setError(null);
          return await spiritualHelpers.sendSpiritualRequest(payload);
        } catch {
          return undefined;
        }
      }

      if (isFinancialContext) {
        try {
          setError(null);
          return await financialHelpers.sendFinancialRequest(payload);
        } catch {
          return undefined;
        }
      }

      if (isMonthlyFeeContext) {
        try {
          setError(null);
          return await monthlyFeeHelpers.sendMonthlyFeeRequest(payload);
        } catch {
          return undefined;
        }
      }

      if (isBankAccountContext) {
        try {
          setError(null);
          return await bankAccountHelpers.sendBankAccountRequest(payload);
        } catch {
          return undefined;
        }
      }

      if (isRitualBathContext) {
        try {
          setError(null);
          return await ritualBathHelpers.sendRitualBathRequest(payload);
        } catch {
          return undefined;
        }
      }

      if (!terreiroId) return undefined;

      try {
        setError(null);
        const response = await sendMessageMutation({
          terreiroId,
          payload: {
            entityType: "inventory_item",
            knownLocations,
            knownCategories,
            ...(inventoryEditTargetId
              ? { mode: "edit" as const, targetEntityId: inventoryEditTargetId }
              : {}),
            ...payload,
          },
        });
        handleResponseUpdate(response);
        return response;
      } catch (err: unknown) {
        const nextError = extractAxiosApiError(err);
        setError(nextError);
        setMessages((prev) => [
          ...prev,
          createMessage("system", nextError.message),
        ]);
        trackInventoryError("form_chat_submit_error", productBase, err);
        return undefined;
      }
    },
    [
      activeContext,
      activityHelpers,
      eventHelpers,
      spiritualHelpers,
      financialHelpers,
      monthlyFeeHelpers,
      bankAccountHelpers,
      ritualBathHelpers,
      canCreate,
      handleResponseUpdate,
      inventoryEditTargetId,
      isActivityContext,
      isEventContext,
      isFinancialContext,
      isBankAccountContext,
      isMonthlyFeeContext,
      isRitualBathContext,
      isSpiritualContext,
      isAtLimit,
      knownCategories,
      knownLocations,
      limitFeature,
      limitMessage,
      mediumId,
      productBase,
      sendMessageMutation,
      terreiroId,
    ],
  );
  sendInventoryRequestRef.current = sendChatRequest;

  const handleSelectOption = useCallback(
    async (selection: FormChatSelectionPayload) => {
      setMessages((prev) => [
        ...prev,
        createMessage("user", selection.optionLabel),
      ]);

      const isEditing = editMode === "await-value" && editingField;
      const fieldToEdit = editingField;
      if (isEditing) {
        setEditMode("none");
        setEditingField(null);
      }

      if (!sessionId && messages.length <= 1) {
        if (isFinancialContext || isMonthlyFeeContext || isBankAccountContext) {
          trackFinancialEvent("financial_form_chat_started", productBase, {
            transaction_type: financialAnalyticsType,
          });
        } else if (isActivityContext) {
          trackActivitiesEvent("activity_form_chat_started", productBase);
        } else if (isEventContext) {
          trackEventsEvent("event_form_chat_started", productBase);
        } else if (isSpiritualContext) {
          trackSpiritualProfileEvent("spiritual_profile_form_chat_started", {
            terreiroId,
            mediumId,
            sourcePage: productBase.sourcePage,
          });
        } else if (isRitualBathContext) {
          trackRitualBathsEvent("ritual_bath_form_chat_started", productBase, {
            source: "form_chat",
          });
        } else {
          trackInventoryEvent("form_chat_started", productBase);
        }
      }
      if (isFinancialContext || isMonthlyFeeContext || isBankAccountContext) {
        trackFinancialEvent("financial_form_chat_field_collected", productBase, {
          transaction_type: financialAnalyticsType,
        });
      } else if (isActivityContext) {
        trackActivitiesEvent("activity_form_chat_field_collected", productBase);
      } else if (isEventContext) {
        trackEventsEvent("event_form_chat_field_collected", productBase);
      } else if (isSpiritualContext) {
        trackSpiritualProfileEvent(
          "spiritual_profile_form_chat_field_collected",
          {
            terreiroId,
            mediumId,
            sourcePage: productBase.sourcePage,
          },
        );
      } else if (isRitualBathContext) {
        trackRitualBathsEvent("ritual_bath_form_chat_field_collected", productBase, {
          source: "form_chat",
        });
      } else {
        trackInventoryEvent("form_chat_field_collected", productBase);
      }

      await sendChatRequest({
        sessionId,
        message: "",
        selection,
        ...(isEditing
          ? {
              action: "edit_field" as const,
              editField: fieldToEdit || selection.field,
            }
          : {}),
      });
    },
    [
      editMode,
      editingField,
      financialAnalyticsType,
      isActivityContext,
      isEventContext,
      isFinancialContext,
      isBankAccountContext,
      isMonthlyFeeContext,
      isRitualBathContext,
      isSpiritualContext,
      messages.length,
      mediumId,
      productBase,
      sendChatRequest,
      sessionId,
      terreiroId,
    ],
  );

  const beginEditingField = useCallback(
    (field: { key: string; label: string }) => {
      setMessages((prev) => [...prev, createMessage("user", field.label)]);
      setEditingField(field.key);
      setActiveField(field.key);
      const localFieldInput = isRitualBathContext
        ? ritualBathHelpers.getFieldInput(field.key)
        : isBankAccountContext
        ? bankAccountHelpers.getFieldInput(field.key)
        : isMonthlyFeeContext
        ? monthlyFeeHelpers.getFieldInput(field.key)
        : isFinancialContext
        ? financialHelpers.getFieldInput(field.key)
        : resolveEditFieldInput(
            field.key,
            isSpiritualContext
              ? spiritualHelpers.spiritualReferenceData
              : inventoryReferenceData,
          );
      setFieldInput(localFieldInput);

      const isListField = isRitualBathContext
        ? ritualBathHelpers.isListField(field.key)
        : isBankAccountContext
        ? bankAccountHelpers.isListField(field.key)
        : isMonthlyFeeContext
        ? monthlyFeeHelpers.isListField(field.key)
        : isFinancialContext
        ? financialHelpers.isListField(field.key)
        : isListSelectField(field.key);
      if (isListField) {
        setEditMode("await-value");
        setMessages((prev) => [
          ...prev,
          createMessage(
            "assistant",
            `Selecione o novo valor para ${field.label} na lista.`,
          ),
        ]);
      } else {
        setEditMode("await-value");
        const customPrompt = isRitualBathContext
          ? ritualBathHelpers.getEditPrompt(field.key, field.label)
          : undefined;
        setMessages((prev) => [
          ...prev,
          createMessage(
            "assistant",
            customPrompt ?? `Qual o novo valor para ${field.label}?`,
          ),
        ]);
      }
      setQuickReplies(localFieldInput?.quickReplies ?? []);
    },
    [
      bankAccountHelpers,
      financialHelpers,
      inventoryReferenceData,
      isFinancialContext,
      isBankAccountContext,
      isMonthlyFeeContext,
      isRitualBathContext,
      isSpiritualContext,
      monthlyFeeHelpers,
      ritualBathHelpers,
      spiritualHelpers.spiritualReferenceData,
    ],
  );

  const handleSendMessage = useCallback(
    async (message: string, options?: FormChatSendOptions) => {
      if (options?.image && activeContext.entityType === "inventory_item") {
        const isEditingImage = editMode === "await-value" && editingField === "image";
        setEditMode("none");
        setEditingField(null);
        setInventoryImage(options.image);
        const optionLabel = "Foto adicionada";
        setMessages((prev) => [...prev, createMessage("user", optionLabel)]);
        trackInventoryEvent("form_chat_field_collected", productBase);
        await sendChatRequest({
          sessionId,
          message: "",
          selection: { field: "image", optionId: "attached", optionLabel },
          ...(isEditingImage
            ? { action: "edit_field" as const, editField: "image" }
            : {}),
        });
        return;
      }

      if (options?.image && isRitualBathContext) {
        const isEditingImage =
          editMode === "await-value" && editingField === "bathImage";
        setEditMode("none");
        setEditingField(null);
        ritualBathHelpers.attachImage(options.image);
        const optionLabel = "Foto adicionada";
        setMessages((prev) => [...prev, createMessage("user", optionLabel)]);
        trackRitualBathsEvent("ritual_bath_form_chat_field_collected", productBase, {
          source: "form_chat",
        });
        await sendChatRequest({
          sessionId,
          message: "",
          selection: { field: "bathImage", optionId: "attached", optionLabel },
          ...(isEditingImage
            ? { action: "edit_field" as const, editField: "bathImage" }
            : {}),
        });
        return;
      }

      const trimmed = message.trim();
      if (!trimmed) return;

      // Step 1 of edit: the user picked which field to change.
      if (editMode === "select-field") {
        const editableFields = isEventContext
          ? eventHelpers.editableFields
          : isActivityContext
            ? activityHelpers.editableFields
            : isSpiritualContext
              ? spiritualHelpers.editableFields
              : (financialEditableFields ?? EDITABLE_FIELDS);
        const field = editableFields.find(
          (item) => item.label.toLowerCase() === trimmed.toLowerCase(),
        );
        if (field) {
          beginEditingField(field);
          return;
        }
        setEditMode("none");
        setEditingField(null);
      }

      setMessages((prev) => [...prev, createMessage("user", trimmed)]);

      // Step 2 of edit: the user typed the new value for the chosen field.
      if (editMode === "await-value" && editingField) {
        const fieldToEdit = editingField;
        setEditMode("none");
        setEditingField(null);
        if (fieldToEdit === "image") setInventoryImage(null);
        await sendChatRequest({
          sessionId,
          message: trimmed,
          action: "edit_field",
          editField: fieldToEdit,
        });
        return;
      }

      if (!sessionId && messages.length <= 1) {
        if (isFinancialContext || isMonthlyFeeContext || isBankAccountContext) {
          trackFinancialEvent("financial_form_chat_started", productBase, {
            transaction_type: financialAnalyticsType,
          });
        } else if (isActivityContext) {
          trackActivitiesEvent("activity_form_chat_started", productBase);
        } else if (isEventContext) {
          trackEventsEvent("event_form_chat_started", productBase);
        } else if (isSpiritualContext) {
          trackSpiritualProfileEvent("spiritual_profile_form_chat_started", {
            terreiroId,
            mediumId,
            sourcePage: productBase.sourcePage,
          });
        } else if (isRitualBathContext) {
          trackRitualBathsEvent("ritual_bath_form_chat_started", productBase, {
            source: "form_chat",
          });
        } else {
          trackInventoryEvent("form_chat_started", productBase);
        }
      }
      if (isFinancialContext || isMonthlyFeeContext || isBankAccountContext) {
        trackFinancialEvent("financial_form_chat_field_collected", productBase, {
          transaction_type: financialAnalyticsType,
        });
      } else if (isActivityContext) {
        trackActivitiesEvent("activity_form_chat_field_collected", productBase);
      } else if (isEventContext) {
        trackEventsEvent("event_form_chat_field_collected", productBase);
      } else if (isSpiritualContext) {
        trackSpiritualProfileEvent(
          "spiritual_profile_form_chat_field_collected",
          {
            terreiroId,
            mediumId,
            sourcePage: productBase.sourcePage,
          },
        );
      } else if (isRitualBathContext) {
        trackRitualBathsEvent("ritual_bath_form_chat_field_collected", productBase, {
          source: "form_chat",
        });
      } else {
        trackInventoryEvent("form_chat_field_collected", productBase);
      }

      if (activeContext.entityType === "inventory_item" && activeField === "image") {
        setInventoryImage(null);
      }
      await sendChatRequest({
        sessionId,
        message: trimmed,
        inputMode: options?.inputMode,
      });
    },
    [
      activeContext.entityType,
      activeField,
      beginEditingField,
      editMode,
      editingField,
      activityHelpers.editableFields,
      eventHelpers.editableFields,
      financialEditableFields,
      financialAnalyticsType,
      spiritualHelpers.editableFields,
      isActivityContext,
      isEventContext,
      isFinancialContext,
      isBankAccountContext,
      isMonthlyFeeContext,
      isRitualBathContext,
      isSpiritualContext,
      messages.length,
      mediumId,
      productBase,
      ritualBathHelpers,
      sendChatRequest,
      sessionId,
      terreiroId,
    ],
  );

  const handleEdit = useCallback(() => {
    if (currentStep !== "review") return;
    setEditMode("select-field");
    setEditingField(null);
  }, [currentStep]);

  const handleCancelEdit = useCallback(() => {
    if (
      shouldLeaveFinancialEditOnCancel(
        location.pathname,
        editMode === "select-field",
      )
    ) {
      handleCloseCreateChat();
      return;
    }
    setEditMode("none");
    setEditingField(null);
    setActiveField(undefined);
    setFieldInput(undefined);
    setQuickReplies([]);
    setMessages((prev) => [
      ...prev,
      createMessage(
        "assistant",
        "Edição cancelada. Revise seus dados e confirme quando estiver pronto.",
      ),
    ]);
  }, [editMode, handleCloseCreateChat, location.pathname]);

  const handleEditField = useCallback(
    (fieldKey: string) => {
      if (currentStep !== "review") return;
      const editableFields = isEventContext
        ? eventHelpers.editableFields
        : isActivityContext
          ? activityHelpers.editableFields
          : isSpiritualContext
            ? spiritualHelpers.editableFields
            : (financialEditableFields ?? EDITABLE_FIELDS);
      const field = editableFields.find((item) => item.key === fieldKey);
      if (!field) return;
      beginEditingField(field);
    },
    [
      beginEditingField,
      currentStep,
      isActivityContext,
      isEventContext,
      isSpiritualContext,
      activityHelpers.editableFields,
      eventHelpers.editableFields,
      financialEditableFields,
      spiritualHelpers.editableFields,
    ],
  );

  const resetConversation = useCallback(() => {
    setSessionId(undefined);
    setMessages(buildInitialMessages(activeContext));
    setFormState({});
    setQuickReplies([]);
    setSummary(undefined);
    setActiveField(activeContext.initialActiveField);
    setFieldInput(undefined);
    setCurrentStep("basic-info");
    setError(null);
    setEditMode("none");
    setEditingField(null);
    setDescriptionAutoGenerated(false);
  }, [activeContext]);

  const handleConfirm = useCallback(async () => {
    if (!sessionId || !activeContext) return;
    const response = await sendChatRequest({
      sessionId,
      message: "",
      action: "confirm",
    });

    if (!response?.isComplete) return;

    if (activeContext.entityType === "inventory_item") {
      const input = {
        data: response.formState as Record<string, unknown>,
        file: inventoryImage ?? undefined,
      };
      try {
        if (inventoryEditTargetId) {
          await updateInventoryByChat(input);
        } else {
          await submitInventoryByChat(input);
        }
      } catch (err: unknown) {
        const nextError = extractAxiosApiError(
          err,
          inventoryEditTargetId
            ? "Não foi possível salvar as alterações. Revise os dados e tente novamente."
            : "Não foi possível criar o item. Revise os dados e tente novamente.",
        );
        setError(nextError);
        setMessages((prev) => [...prev, createMessage("system", nextError.message)]);
        return;
      }
      resetConversation();
      assistantControlRef.current?.close();
    } else if (activeContext.entityType === "activity") {
      await submitActivityByChat({ data: response.formState });
      resetConversation();
    } else if (activeContext.entityType === "event") {
      await eventHelpers.handleConfirmEvent();
    }
  }, [
    activeContext,
    eventHelpers,
    inventoryEditTargetId,
    inventoryImage,
    resetConversation,
    sendChatRequest,
    sessionId,
    submitActivityByChat,
    submitInventoryByChat,
    updateInventoryByChat,
  ]);

  const handleTranscribeAudio = useCallback(
    async (audio: Blob) => {
      if (isActivityContext) {
        return activityHelpers.transcribeAudio(audio);
      }
      if (isFinancialContext) {
        return financialHelpers.transcribeAudio(audio);
      }
      if (isMonthlyFeeContext) {
        return monthlyFeeHelpers.transcribeAudio(audio);
      }
      if (isBankAccountContext) {
        return bankAccountHelpers.transcribeAudio(audio);
      }
      if (isRitualBathContext) {
        return ritualBathHelpers.transcribeAudio(audio);
      }
      if (!terreiroId) {
        throw new Error("Terreiro context required");
      }
      const response = await formChatRepository.transcribeAudio(
        terreiroId,
        audio,
      );
      return response.transcript;
    },
    [
      activityHelpers,
      bankAccountHelpers,
      financialHelpers,
      isActivityContext,
      isFinancialContext,
      isBankAccountContext,
      isMonthlyFeeContext,
      isRitualBathContext,
      monthlyFeeHelpers,
      ritualBathHelpers,
      terreiroId,
    ],
  );

  const handleReset = resetConversation;

  const effectiveActiveField =
    editMode === "await-value" && editingField ? editingField : activeField;

  const fieldReferenceData = isSpiritualContext
    ? spiritualHelpers.spiritualReferenceData
    : inventoryReferenceData;

  const fieldOptions = useMemo(
    () =>
      isRitualBathContext
        ? ritualBathHelpers.getFieldOptions(effectiveActiveField)
        : isBankAccountContext
        ? bankAccountHelpers.getFieldOptions(effectiveActiveField)
        : isMonthlyFeeContext
        ? monthlyFeeHelpers.getFieldOptions(effectiveActiveField)
        : isFinancialContext
          ? financialHelpers.getFieldOptions(effectiveActiveField)
          : resolveFieldOptions(effectiveActiveField, fieldReferenceData),
    [
      bankAccountHelpers,
      effectiveActiveField,
      fieldReferenceData,
      financialHelpers,
      isFinancialContext,
      isBankAccountContext,
      isMonthlyFeeContext,
      isRitualBathContext,
      monthlyFeeHelpers,
      ritualBathHelpers,
    ],
  );

  const effectiveFieldInput = useMemo(() => {
    if (
      (isActivityContext || isEventContext) &&
      (effectiveActiveField === "memberIds" ||
        effectiveActiveField === "insumos" ||
        effectiveActiveField === "activities" ||
        effectiveActiveField === "location" ||
        effectiveActiveField === "type")
    ) {
      return undefined;
    }

    if (isFinancialContext) {
      return financialHelpers.resolveFieldInput(effectiveActiveField, fieldInput);
    }

    if (isMonthlyFeeContext) {
      return monthlyFeeHelpers.resolveFieldInput(effectiveActiveField, fieldInput);
    }

    if (isBankAccountContext) {
      return bankAccountHelpers.resolveFieldInput(effectiveActiveField, fieldInput);
    }

    if (isRitualBathContext) {
      return ritualBathHelpers.resolveFieldInput(effectiveActiveField, fieldInput);
    }

    return resolveFieldInput(
      effectiveActiveField,
      fieldReferenceData,
      fieldInput,
    );
  }, [
    bankAccountHelpers,
    effectiveActiveField,
    fieldInput,
    fieldReferenceData,
    financialHelpers,
    isActivityContext,
    isEventContext,
    isBankAccountContext,
    isFinancialContext,
    isMonthlyFeeContext,
    isRitualBathContext,
    monthlyFeeHelpers,
    ritualBathHelpers,
  ]);

  const quickReplyOptions = useMemo(() => {
    if (editMode === "select-field") {
      const editableFields = isEventContext
        ? eventHelpers.editableFields
        : isActivityContext
          ? activityHelpers.editableFields
          : isSpiritualContext
            ? spiritualHelpers.editableFields
            : (financialEditableFields ?? EDITABLE_FIELDS);
      return editableFields.map((field) => field.label);
    }
    return resolveDisplayedQuickReplies(quickReplies, effectiveFieldInput);
  }, [
    editMode,
    effectiveFieldInput,
    activityHelpers.editableFields,
    eventHelpers.editableFields,
    financialEditableFields,
    isActivityContext,
    isEventContext,
    isSpiritualContext,
    quickReplies,
    spiritualHelpers.editableFields,
  ]);

  const selectedOption = useMemo(
    () =>
      resolveSelectedOption(
        effectiveActiveField,
        formState as Record<string, unknown>,
        fieldOptions,
      ),
    [effectiveActiveField, fieldOptions, formState],
  );

  const editInteraction =
    editMode === "none" ? null : (editMode as "select-field" | "await-value");

  const editingFieldLabel = useMemo(() => {
    if (!editingField) return undefined;
    const editableFields = isEventContext
      ? eventHelpers.editableFields
      : isActivityContext
        ? activityHelpers.editableFields
        : isSpiritualContext
          ? spiritualHelpers.editableFields
          : (financialEditableFields ?? EDITABLE_FIELDS);
    return editableFields.find((field) => field.key === editingField)?.label;
  }, [
    editingField,
    activityHelpers.editableFields,
    eventHelpers.editableFields,
    financialEditableFields,
    spiritualHelpers.editableFields,
    isActivityContext,
    isEventContext,
    isSpiritualContext,
  ]);

  const editableFieldOptions = useMemo(() => {
    if (isActivityContext) {
      return activityHelpers.editableFieldOptions;
    }
    if (isEventContext) {
      return eventHelpers.editableFieldOptions;
    }
    if (isFinancialContext) {
      return financialHelpers.editableFieldOptions;
    }
    if (isMonthlyFeeContext) {
      return monthlyFeeHelpers.editableFieldOptions;
    }
    if (isBankAccountContext) {
      return bankAccountHelpers.editableFieldOptions;
    }
    if (isRitualBathContext) {
      return ritualBathHelpers.editableFieldOptions;
    }
    const editableFields = isSpiritualContext
      ? spiritualHelpers.editableFields
      : EDITABLE_FIELDS;
    const inventorySummary = summary as InventoryChatSummary | undefined;

    return editableFields.map((field) => {
      const summaryValue =
        inventorySummary &&
        field.key in inventorySummary &&
        inventorySummary[field.key as keyof InventoryChatSummary] !== undefined
          ? String(inventorySummary[field.key as keyof InventoryChatSummary])
          : undefined;
      const formValue = formState[field.key as keyof FormChatState];
      if (field.key === "estimatedUnitCost") {
        const unitCost = inventorySummary?.estimatedUnitCost ?? formState.estimatedUnitCost;
        return {
          key: field.key,
          label: field.label,
          currentValue:
            typeof unitCost === "number"
              ? unitCost.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
              : "Não informado",
        };
      }
      const currentValue =
        summaryValue ??
        (typeof formValue === "string" || typeof formValue === "number"
          ? String(formValue)
          : undefined);

      return {
        key: field.key,
        label: field.label,
        currentValue,
      };
    });
  }, [
    activityHelpers.editableFieldOptions,
    eventHelpers.editableFieldOptions,
    financialHelpers.editableFieldOptions,
    monthlyFeeHelpers.editableFieldOptions,
    bankAccountHelpers.editableFieldOptions,
    ritualBathHelpers.editableFieldOptions,
    spiritualHelpers.editableFields,
    formState,
    isActivityContext,
    isEventContext,
    isBankAccountContext,
    isFinancialContext,
    isMonthlyFeeContext,
    isRitualBathContext,
    isSpiritualContext,
    summary,
  ]);

  const hideInventoryFab =
    activeContext.entityType === "inventory_item" &&
    !isCreateRoute &&
    isInventoryTabRoot(location.pathname);

  useEffect(() => {
    return registerFormChatAssistantOpen(() => {
      setFlowOpened(true);
      assistantControlRef.current?.open();
    });
  }, []);

  if (!canShowAssistant || isBankAccountLimitBlocked || isRitualBathLimitBlocked) {
    return null;
  }

  const isChatLoading = isEventContext
    ? eventHelpers.isPending
    : isActivityContext
      ? activityHelpers.isPending
      : isSpiritualContext
        ? spiritualHelpers.isPending
        : isFinancialContext
          ? financialHelpers.isPending
          : isMonthlyFeeContext
            ? monthlyFeeHelpers.isPending
            : isBankAccountContext
              ? bankAccountHelpers.isPending
              : isRitualBathContext
                ? ritualBathHelpers.isPending
                : isPending;

  const chatSummaryCard = isEventContext
    ? eventHelpers.summaryCard
    : isActivityContext
      ? activityHelpers.summaryCard
      : isSpiritualContext
        ? spiritualHelpers.summaryCard
        : isFinancialContext
          ? financialHelpers.summaryCard
          : isMonthlyFeeContext
            ? monthlyFeeHelpers.summaryCard
            : isBankAccountContext
              ? bankAccountHelpers.summaryCard
              : isRitualBathContext
                ? ritualBathHelpers.summaryCard
                : undefined;

  const pickerEditField =
    (isEventContext || isActivityContext) && editMode === "await-value"
      ? editingField
      : null;
  const quorumMemberCount = Array.isArray(formState.memberIds)
    ? formState.memberIds.length
    : 0;
  const sendEditValue = (value: number | string) => handleSendMessage(String(value));
  let editValuePanel: ReactNode;
  if (pickerEditField === "minConfirmedMembers") {
    editValuePanel = (
      <FormChatNumberPicker
        key="quorum-edit"
        title="Quórum mínimo"
        label="Quórum mínimo de confirmações"
        value={Number(formState.minConfirmedMembers) || 1}
        min={1}
        max={quorumMemberCount > 0 ? quorumMemberCount : undefined}
        helperText={
          quorumMemberCount > 0
            ? `Entre 1 e ${quorumMemberCount} médium(ns) selecionado(s).`
            : undefined
        }
        isLoading={isChatLoading}
        onConfirm={sendEditValue}
      />
    );
  } else if (pickerEditField === "qrCheckinEnabled") {
    editValuePanel = (
      <FormChatChoicePicker
        key="checkin-edit"
        title="Check-in"
        description="Os médiuns confirmam presença lendo o QR code no local."
        options={[
          { value: "Sim", label: "Ativar check-in" },
          { value: "Não", label: "Desativar check-in" },
        ]}
        selectedValue={
          typeof formState.qrCheckinEnabled === "boolean"
            ? formState.qrCheckinEnabled
              ? "Sim"
              : "Não"
            : undefined
        }
        isLoading={isChatLoading}
        onSelect={(option) => sendEditValue(option.value)}
      />
    );
  } else if (pickerEditField && CHECKIN_WINDOW_EDIT_FIELDS.has(pickerEditField)) {
    const isStartWindow = pickerEditField === "checkinStartWindowMinutes";
    const minutes = formState[pickerEditField as keyof FormChatState];
    editValuePanel = (
      <FormChatNumberPicker
        key={`${pickerEditField}-edit`}
        title={isStartWindow ? "Check-in antes do início" : "Check-in depois do início"}
        label="Minutos"
        value={typeof minutes === "number" ? minutes : isStartWindow ? 10 : 15}
        min={0}
        max={120}
        helperText={
          isStartWindow
            ? "Quantos minutos antes do início o check-in abre (0 a 120)."
            : "Quantos minutos depois do início o check-in fecha (0 a 120)."
        }
        isLoading={isChatLoading}
        onConfirm={sendEditValue}
      />
    );
  }

  const chatFieldPanel =
    editValuePanel ??
    (isEventContext
      ? eventHelpers.fieldPanel
      : isActivityContext
        ? activityHelpers.fieldPanel
        : isFinancialContext
          ? financialHelpers.getFieldPanel({
              field: effectiveActiveField,
              isLoading: isChatLoading,
              onValue: sendEditValue,
              onSkip: () => handleSendMessage("Pular"),
            })
          : isMonthlyFeeContext
            ? monthlyFeeHelpers.getFieldPanel({
                field: effectiveActiveField,
                isLoading: isChatLoading,
                onValue: sendEditValue,
              })
            : undefined);

  const chatStepOrder = isEventContext
    ? eventHelpers.stepOrder
    : isActivityContext
      ? activityHelpers.stepOrder
      : undefined;

  return (
    <FormChatAssistant
      title={activeContext.title}
      subtitle={activeContext.subtitle}
      displayMode={isFullPageRoute ? "fullscreen" : "fab"}
      hideFab={hideInventoryFab}
      messages={messages}
      quickReplies={quickReplyOptions}
      summary={
        isActivityContext ||
        isEventContext ||
        isSpiritualContext ||
        isFinancialContext ||
        isMonthlyFeeContext ||
        isBankAccountContext ||
        isRitualBathContext ||
        !summary
          ? undefined
          : {
              ...(summary as InventoryChatSummary),
              imageUrl:
                formState.image === "attached"
                  ? (inventoryImagePreviewUrl ?? formState.imageUrl ?? null)
                  : formState.image === "skipped"
                    ? null
                    : undefined,
            }
      }
      summaryCard={chatSummaryCard}
      fieldPanel={chatFieldPanel}
      hideListSelectBar={isActivityContext || isEventContext}
      currentStep={currentStep}
      stepOrder={chatStepOrder}
      isLoading={isChatLoading}
      error={error}
      confirmLabel={activeContext.confirmLabel}
      editLabel="Editar"
      descriptionAutoGenerated={descriptionAutoGenerated}
      voiceEnabled={shouldEnableVoice}
      fabAnchor={
        isActivityContext || isEventContext || isSpiritualContext
          ? "corner"
          : "default"
      }
      fabIcon={isActivityContext || isEventContext ? "add" : "logo"}
      fabAriaLabel={
        isEventContext
          ? "Adicionar evento"
          : isActivityContext
            ? "Adicionar"
            : undefined
      }
      activeField={effectiveActiveField}
      fieldInput={effectiveFieldInput}
      fieldOptions={fieldOptions}
      selectedOption={selectedOption}
      userAvatarUrl={userAvatarUrl}
      defaultOpen={isFullPageRoute}
      onTranscribeAudio={shouldEnableVoice ? handleTranscribeAudio : undefined}
      onSendMessage={handleSendMessage}
      onSelectOption={handleSelectOption}
      onConfirm={
        isActivityContext
          ? activityHelpers.handleConfirmActivity
          : isEventContext
            ? eventHelpers.handleConfirmEvent
            : isSpiritualContext
              ? spiritualHelpers.handleConfirmSpiritualProfile
              : isFinancialContext
                ? financialHelpers.handleConfirmFinancial
                : isMonthlyFeeContext
                  ? monthlyFeeHelpers.handleConfirmMonthlyFee
                  : isBankAccountContext
                    ? bankAccountHelpers.handleConfirmBankAccount
                    : isRitualBathContext
                      ? ritualBathHelpers.handleConfirmRitualBath
                      : handleConfirm
      }
      onEdit={handleEdit}
      onCancelEdit={handleCancelEdit}
      onEditField={handleEditField}
      editInteraction={editInteraction}
      editingFieldLabel={editingFieldLabel}
      editableFieldOptions={editableFieldOptions}
      onReset={handleReset}
      onClose={isFullPageRoute ? handleCloseCreateChat : undefined}
      controlRef={assistantControlRef}
    />
  );
};

export default FormChatAssistantProvider;
